/**
 * Who may edit what, for the CURRENT session.
 *
 * This is separate from the section cache on purpose, and the separation is load-bearing.
 * Content is keyed by (name, lang) — two identities share one cache entry. `canWrite` is
 * derived from the session, so if it lived in that entry a persisted anonymous snapshot
 * would restore over an admin's and every pencil would vanish. That bug happened.
 *
 * Rule: content caches and persists. Permission does neither.
 */
import { useSyncExternalStore } from 'react';
import type { SectionQueryResult } from '@framework/core';

const capabilities = new Map<string, boolean>();
const listeners = new Set<() => void>();
let snapshot: Record<string, boolean> = {};

function publish(): void {
  snapshot = Object.fromEntries(capabilities);
  listeners.forEach((l) => l());
}

/** Refreshed from every QUERY response. */
export function setCapabilities(results: readonly SectionQueryResult[]): void {
  let changed = false;
  for (const r of results) {
    // A MISSING canWrite (FORBIDDEN, NOT_FOUND) means NOT writable. It must not be
    // skipped, or a previously-true capability survives losing access to the section.
    const next = r.canWrite === true;
    if (capabilities.get(r.name) !== next) { capabilities.set(r.name, next); changed = true; }
  }
  if (changed) publish();
}

/** Called on sign-in, sign-out, identity change and flush. */
export function clearCapabilities(): void {
  capabilities.clear();
  publish();
}

export const useCapabilities = (): Record<string, boolean> =>
  useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => snapshot,
    () => snapshot,
  );
